import { isMapId, mapInfo, type MapId } from "@69ly/maps";
import { COMBO_SPREAD_WARN, ComboInput, comboSpread, formatClock, MAX_CLIP_BYTES, MAX_COMBO_STEPS, parseTimestamp, parseYouTube, type Area, type Combo, type Importance, type Side, type Stuff } from "@69ly/shared";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";
import { AreaField, LandingList, mostCommonArea } from "../components/AreaField.tsx";
import { ClipPlayer } from "../components/ClipPlayer.tsx";
import { ClipCodecNote } from "../components/ClipCodecNote.tsx";
import { DropZone, VIDEO_ACCEPT } from "../components/DropZone.tsx";
import { GrenadeGlyph } from "../components/GrenadeGlyph.tsx";
import { ImportanceField } from "../components/Importance.tsx";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { move, RowMoves, StuffPicker } from "../components/StuffPicker.tsx";
import { parseWindows, windowDrafts, WindowsEditor, type WindowDraft } from "../components/WindowsEditor.tsx";
import { useCallouts } from "../lib/callouts.ts";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useLeave } from "../lib/leave.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { uploadComboClip, useCombo, useRemoveComboClip, useSaveCombo } from "../lib/teamplay-queries.ts";
import { Segmented } from "./StuffForm.tsx";

interface StepDraft {
  stuff: Stuff;
  note: string;
}

function ComboForm({ map, combo, singles }: { map: MapId; combo?: Combo; singles: Stuff[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leave = useLeave();
  const { scope } = useCurrentTeam();
  const save = useSaveCombo(combo?.id);
  const info = mapInfo(map);

  const [title, setTitle] = useState(combo?.title ?? "");
  const [side, setSide] = useState<Side>(combo?.side ?? "t");
  // The area follows the throws' areas unless the player picks one.
  const [areaChoice, setAreaChoice] = useState<Area | null>(() =>
    combo && combo.area !== mostCommonArea(combo.steps.map((s) => s.stuff.area)) ? combo.area : null,
  );
  const [notes, setNotes] = useState(combo?.notes ?? "");
  const [importance, setImportance] = useState<Importance>(combo?.importance ?? "useful");
  const [steps, setSteps] = useState<StepDraft[]>(combo?.steps.map((s) => ({ stuff: s.stuff, note: s.note })) ?? []);
  const [windows, setWindows] = useState<WindowDraft[]>(windowDrafts(combo?.windows));
  const location = useLocation();
  // A failed video upload after the save comes back here with the reason.
  const [error, setError] = useState<string | null>(() => (location.state as { uploadError?: string } | null)?.uploadError ?? null);
  // A video of the whole combo: YouTube, or its own upload (sent after the save).
  const [youtubeUrl, setYoutubeUrl] = useState(
    combo?.youtube ? `https://youtu.be/${combo.youtube.id}${combo.youtube.start != null ? `?t=${combo.youtube.start}` : ""}` : "",
  );
  const [clipEnd, setClipEnd] = useState(combo?.youtube?.end != null ? formatClock(combo.youtube.end) : "");
  const [clipFile, setClipFile] = useState<File | null>(null);
  const [clipProgress, setClipProgress] = useState<number | null>(null);
  const removeClip = useRemoveComboClip(combo?.id ?? "");
  const youtube = youtubeUrl.trim() ? parseYouTube(youtubeUrl) : null;
  const pickClip = (f: File | null) => {
    if (f && f.size > MAX_CLIP_BYTES) return setError(t("form.clipTooLarge", { mb: MAX_CLIP_BYTES / 1024 / 1024 }));
    setError(null);
    setClipFile(f);
  };

  const callouts = useCallouts();
  const autoArea = mostCommonArea(steps.map((s) => s.stuff.area));
  const landings = steps.map((s) => callouts.places(map, s.stuff).to);
  const area: Area = areaChoice ?? autoArea ?? "other";
  const spread = comboSpread(steps.map((s) => s.stuff.throw));
  const full = steps.length >= MAX_COMBO_STEPS;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsedWindows = parseWindows(windows);
    if (youtubeUrl.trim() && !youtube) return setError(t("form.youtubeBad"));
    const end = clipEnd.trim() ? parseTimestamp(clipEnd) : null;
    const input = ComboInput.safeParse({
      map,
      side,
      area,
      title,
      notes,
      importance,
      steps: steps.map((s) => ({ stuffId: s.stuff.id, note: s.note })),
      windows: parsedWindows,
      youtube: youtube ? { id: youtube.id, start: youtube.start, end } : null,
    });
    if (!input.success) return setError(input.error.issues[0]?.message ?? t("errors.invalid_input"));
    try {
      const saved = await save.mutateAsync(combo ? input.data : { ...input.data, scope });
      if (clipFile) {
        setClipProgress(0);
        try {
          await uploadComboClip(saved.id, clipFile, setClipProgress);
        } catch (err) {
          const reason = err instanceof Error && "code" in err && err.code === "too_large" ? t("form.clipTooLarge", { mb: MAX_CLIP_BYTES / 1024 / 1024 }) : errorText(t, err);
          // The combo is saved: say what failed on its edit page.
          const uploadError = t("form.uploadFailed", { name: clipFile.name, reason });
          setClipProgress(null);
          setError(uploadError);
          return navigate(`/combos/${saved.id}/edit`, { replace: true, state: { uploadError } });
        }
      }
      if (combo) leave.back(`/combos/${saved.id}`);
      else leave.to(`/combos/${saved.id}`);
    } catch (err) {
      setError(errorText(t, err));
    }
  };

  return (
    <form className="page stuff-form" onSubmit={submit} noValidate>
      <div className="page-head">
        <h1 className="page-title">{combo ? t("comboForm.editTitle", { title: combo.title }) : t("comboForm.newTitle", { map: info.name })}</h1>
        <div className="row">
          <button type="button" className="btn btn-quiet" onClick={() => leave.back(combo ? `/combos/${combo.id}` : `/library/${map}?type=combos`)}>
            {t("form.cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={save.isPending || clipProgress !== null}>
            {clipProgress !== null ? t("form.uploadingClip", { percent: Math.round(clipProgress * 100) }) : save.isPending ? t("form.saving") : t("comboForm.save")}
          </button>
        </div>
      </div>
      {error && <Banner tone="warning">{error}</Banner>}

      <div className="columns">
        <div className="column-main form-main">
          <section className="card">
            <h2 className="section-title">1 · {t("comboForm.stepThrows")}</h2>
            <p className="muted">{t("comboForm.stepThrowsHelp")}</p>
            {steps.length === 0 ? (
              <p className="muted small">{t("comboForm.noThrows")}</p>
            ) : (
              <ol className="draft-steps">
                {steps.map((s, i) => (
                  <li key={s.stuff.id} className="draft-step">
                    <span className="step-number">{i + 1}</span>
                    <GrenadeGlyph grenade={s.stuff.grenade} size={14} />
                    <span className="draft-step-title">{s.stuff.title}</span>
                    <input
                      className="draft-step-note"
                      value={s.note}
                      maxLength={140}
                      placeholder={t("comboForm.notePlaceholder")}
                      aria-label={t("comboForm.noteFor", { title: s.stuff.title })}
                      onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
                    />
                    <RowMoves
                      index={i}
                      count={steps.length}
                      label={s.stuff.title}
                      onMove={(a, b) => setSteps(move(steps, a, b))}
                      onRemove={() => setSteps(steps.filter((_, j) => j !== i))}
                    />
                  </li>
                ))}
              </ol>
            )}
            {spread > COMBO_SPREAD_WARN && <Banner tone="warning">{t("comboForm.spread", { meters: Math.round(spread / 32) })}</Banner>}
            <h3 className="eyebrow">{full ? t("comboForm.full", { max: MAX_COMBO_STEPS }) : t("comboForm.addFrom", { map: info.name })}</h3>
            <StuffPicker
              stuffs={singles}
              side={side}
              exclude={new Set(steps.map((s) => s.stuff.id))}
              disabled={full}
              onPick={(p) => p.kind === "single" && setSteps([...steps, { stuff: p.stuff, note: "" }])}
            />
            {singles.length === 0 && (
              <Link to={`/library/${map}/new`} className="btn btn-secondary btn-small">
                {t("mapView.new")}
              </Link>
            )}
          </section>

          <section className="card">
            <h2 className="section-title">2 · {t("form.stepDetails")}</h2>
            <label className="field">
              <span>{t("form.titleLabel")}</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("comboForm.titlePlaceholder")} maxLength={60} />
            </label>
            <div className="field-row">
              <Segmented label={t("form.side")} options={["t", "ct"] as const} value={side} onChange={setSide} render={(s) => t(`sideShort.${s}`)} />
              <AreaField
                label={t("form.area")}
                auto={autoArea}
                choice={areaChoice}
                onChoice={setAreaChoice}
                autoHelp={t("areaField.comboHelp")}
                noAutoHelp={t("areaField.comboNone")}
                detail={<LandingList places={landings} />}
              />
            </div>
            <ImportanceField value={importance} onChange={setImportance} />
            <label className="field">
              <span>{t("form.notes")}</span>
              <textarea rows={3} value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
            </label>
          </section>
        </div>

        <aside className="column-side form-side">
          <section className="card">
            <h2 className="section-title">{t("form.windows")}</h2>
            <p className="muted small">{t("form.windowsHelp")}</p>
            <WindowsEditor windows={windows} onChange={setWindows} />
          </section>

          <section className="card">
            <h2 className="section-title">{t("comboForm.video")}</h2>
            <p className="muted small">{t("comboForm.videoHelp")}</p>
            <label className="field">
              <span>{t("form.youtube")}</span>
              <input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtu.be/…?t=42" />
            </label>
            {youtubeUrl.trim() && !youtube && <p className="field-error">{t("form.youtubeBad")}</p>}
            {youtube && (
              <label className="field">
                <span>{t("form.clipEnd")}</span>
                <input className="mono" value={clipEnd} onChange={(e) => setClipEnd(e.target.value)} placeholder="1:05" />
              </label>
            )}
            <h3 className="eyebrow">{t("form.clip")}</h3>
            <p className="muted small">{t("form.clipHelp", { mb: MAX_CLIP_BYTES / 1024 / 1024 })}</p>
            {combo?.clip && !clipFile && (
              <div className="clip-current">
                <ClipPlayer src={combo.clip.url} title={t("comboForm.video")}/>
                <button type="button" className="btn btn-quiet btn-small btn-danger" disabled={removeClip.isPending} onClick={() => removeClip.mutate()}>
                  {t("form.removeClip")}
                </button>
              </div>
            )}
            {clipFile && (
              <p className="small">
                <span className="file-name">{clipFile.name}</span> · {(clipFile.size / 1024 / 1024).toFixed(1)} MB{" "}
                <button type="button" className="link-button" onClick={() => setClipFile(null)}>
                  {t("form.removePicture")}
                </button>
              </p>
            )}
            <ClipCodecNote file={clipFile} />
            {clipProgress !== null && <progress className="clip-progress" value={clipProgress} max={1} aria-label={t("form.uploadingClip", { percent: Math.round(clipProgress * 100) })} />}
            <DropZone accept={VIDEO_ACCEPT} hint={t("drop.video")} onFiles={([f]) => pickClip(f)} onRefused={(names) => setError(t("drop.refused", { names: names.join(", ") }))}>
              <label className="btn btn-secondary btn-small file-button">
                {combo?.clip || clipFile ? t("form.replaceClip") : t("form.addClip")}
                <input
                  type="file"
                  accept={VIDEO_ACCEPT.join(",")}
                  aria-label={t("comboForm.videoFile")}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    e.target.value = "";
                    pickClip(f);
                  }}
                />
              </label>
            </DropZone>
          </section>
        </aside>
      </div>
    </form>
  );
}

export function NewComboPage() {
  const { t } = useTranslation();
  const { map } = useParams();
  const { scope } = useCurrentTeam();
  const singles = useStuffs(scope, isMapId(map) ? map : "");
  if (!isMapId(map)) return <Navigate to="/library" replace />;
  if (singles.isPending) return <Loading />;
  if (singles.error) return <ErrorState message={errorText(t, singles.error)} onRetry={() => void singles.refetch()} />;
  return <ComboForm map={map} singles={singles.data} />;
}

export function EditComboPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const combo = useCombo(id);
  const { scope } = useCurrentTeam();
  const comboScope = combo.data ? (combo.data.teamId ?? "personal") : scope;
  const singles = useStuffs(comboScope, combo.data?.map ?? "");
  if (combo.isPending || singles.isPending) return <Loading />;
  if (combo.error) return <ErrorState message={errorText(t, combo.error)} onRetry={() => void combo.refetch()} />;
  if (singles.error) return <ErrorState message={errorText(t, singles.error)} onRetry={() => void singles.refetch()} />;
  if (!isMapId(combo.data.map) || !combo.data.canEdit) return <Navigate to={`/combos/${combo.data.id}`} replace />;
  return <ComboForm map={combo.data.map} combo={combo.data} singles={singles.data} />;
}
